import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Percent } from 'lucide-react';
import type { TaxCategoryToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import {
	useTaxCategoriesList,
	useTaxTypes,
	useTaxStatuses,
	useTaxCategoryCountries,
	useTaxCategoryMutations
} from '~/hooks/use-tax-categories';
import { tokenToForm, type TaxCategoryFormValues } from '~/lib/schemas/tax-category';
import { TaxCategoryEditDialog } from './tax-category-edit-dialog';

/** Tax categories (Κατηγορίες ΦΠΑ) CRUD surface — parity with the legacy admin grid. */
export function TaxCategoriesGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useTaxCategoriesList();
	const { data: taxTypes = [] } = useTaxTypes();
	const { data: taxStatuses = [] } = useTaxStatuses();
	const { data: countries = [] } = useTaxCategoryCountries();
	const { remove } = useTaxCategoryMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<TaxCategoryFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<TaxCategoryToken | null>(null);

	const typeMap = useMemo(() => new Map(taxTypes.map(o => [o.id, o.nm])), [taxTypes]);
	const statusMap = useMemo(() => new Map(taxStatuses.map(o => [o.id, o.nm])), [taxStatuses]);
	const countryMap = useMemo(() => new Map(countries.map(c => [c.id, c.nm])), [countries]);
	const typeOf = (o: TaxCategoryToken) => (o.tt == null ? '' : (typeMap.get(o.tt) ?? ''));
	const statusOf = (o: TaxCategoryToken) => (o.ts == null ? '' : (statusMap.get(o.ts) ?? ''));
	const countryOf = (o: TaxCategoryToken) => (o.ci == null ? '' : (countryMap.get(o.ci) ?? ''));

	const columns = useMemo<SystemGridColumn<TaxCategoryToken>[]>(
		() => [
			{
				key: 'name',
				header: t('prices.taxCategories.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.nm ?? '',
				exportValue: o => o.nm ?? '',
				render: o => (
					<span className="truncate text-sm font-medium text-foreground" title={o.nm ?? ''}>
						{o.nm || '—'}
					</span>
				)
			},
			{
				key: 'type',
				header: t('prices.taxCategories.columns.type'),
				width: '130px',
				sortValue: o => typeOf(o),
				exportValue: o => typeOf(o),
				render: o => <span className="text-sm text-foreground-muted">{typeOf(o) || '—'}</span>
			},
			{
				key: 'status',
				header: t('prices.taxCategories.columns.status'),
				width: '130px',
				sortValue: o => statusOf(o),
				exportValue: o => statusOf(o),
				render: o => <span className="text-sm text-foreground-muted">{statusOf(o) || '—'}</span>
			},
			{
				key: 'country',
				header: t('prices.taxCategories.columns.country'),
				width: '160px',
				sortValue: o => countryOf(o),
				exportValue: o => countryOf(o),
				render: o => <span className="truncate text-sm text-foreground-muted">{countryOf(o) || '—'}</span>
			},
			{
				key: 'rate',
				header: t('prices.taxCategories.columns.rate'),
				width: '100px',
				align: 'right',
				sortValue: o => o.rt ?? 0,
				exportValue: o => o.rt ?? 0,
				render: o => <span className="text-sm tabular-nums text-foreground">{o.rt}</span>
			},
			{
				key: 'visible',
				header: t('prices.taxCategories.columns.visible'),
				width: '90px',
				align: 'center',
				sortValue: o => (o.iv ? 1 : 0),
				exportValue: o => (o.iv ? t('common.yes') : t('common.no')),
				render: o => (o.iv ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>)
			},
			{
				key: 'sort',
				header: t('prices.taxCategories.columns.sort'),
				width: '80px',
				align: 'right',
				sortValue: o => o.si ?? 0,
				exportValue: o => o.si ?? 0,
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{o.si}</span>
			}
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t, typeMap, statusMap, countryMap]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<TaxCategoryToken>
				gridId="tax-categories"
				title={t('prices.taxCategories.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={o => o.id ?? o.nm}
				columns={columns}
				searchPredicate={(o, q) => `${o.nm ?? ''} ${countryOf(o)}`.toLowerCase().includes(q)}
				emptyIcon={<Percent size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('prices.taxCategories.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={o => {
					setEditing(tokenToForm(o));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="tax-categories"
			/>

			<TaxCategoryEditDialog
				open={editOpen}
				onOpenChange={setEditOpen}
				initial={editing}
				taxTypes={taxTypes}
				taxStatuses={taxStatuses}
				countries={countries}
			/>

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('prices.taxCategories.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('prices.taxCategories.deleteBody', { name: deleteRow?.nm || '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
